// @givmo/board — where the provider sends a connect back (CBG-CAL1 slice 1d), at
// `<Board mount>/calendar/connected` (`CALENDAR_CONNECT_RETURN_PAGE`, registered per console host).
//
// The page reads what the provider put in its address — `code` and `state`, or `error` — and says so
// on the same-origin channel. Two cases, and the page can't tell which it is in, so it runs one path:
//
//   * THE POP-UP (the ordinary case). The dialog that started this flow is listening for its own
//     `state`; it answers, takes the code, finishes the flow and shows the answer itself. The page
//     then closes its window (a script-opened window is script-closable, disowned or not).
//   * THE WHOLE TAB (no pop-up). Nobody answers within `RETURN_ACK_WAIT_MS`, so the page finishes the
//     flow itself and shows the answer here, in the same dialog; Done goes back to the page that asked
//     (kept in this tab by `use-calendar-connect.ts`), or to Meetings.
//
// It shows nothing while it waits: in a pop-up the window closes before anything would paint.

import { useEffect, useRef, useState } from 'react';
import { useRouter } from '@tanstack/react-router';
import { Dialog } from '@givmo/design-system';
import { useBoardHost } from '../../board-host';
import { useBoardBasePath, useBoardConnectionOperations } from '../../board-runtime';
import { BOARD_PAGES } from '../../board-routes';
import { boardPath, useSearch } from '../../board-routing';
import {
  CALENDAR_CONNECT_CHANNEL,
  RETURN_ACK_WAIT_MS,
  calendarConnectRedirectUri,
  canUsePopup,
  outcomeOfCompleteFailure,
  outcomeOfProviderError,
  readConnectMessage,
  type ConnectOutcome,
} from '../../calendar/calendar-connect';
import { useRefreshCalendar } from '../../calendar/calendar-queries';
import { CONNECT_RETURN_PATH_KEY } from '../../calendar/use-calendar-connect';
import { calendarWords as w } from '../../calendar/calendar-words';
import { ConnectAnswer } from './connect-dialog';

function text(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/** Wait for the dialog that started this flow to answer; false when nobody does in time. */
function announced(state: string, code: string | null, error: string | null): Promise<boolean> {
  if (!canUsePopup()) return Promise.resolve(false);
  return new Promise((resolve) => {
    const channel = new BroadcastChannel(CALENDAR_CONNECT_CHANNEL);
    const timer = setTimeout(() => {
      channel.close();
      resolve(false);
    }, RETURN_ACK_WAIT_MS);
    channel.onmessage = (event: MessageEvent) => {
      const message = readConnectMessage(event.data);
      if (message?.type !== 'ack' || message.state !== state) return;
      clearTimeout(timer);
      channel.close();
      resolve(true);
    };
    channel.postMessage({ type: 'return', state, code, error });
  });
}

function takeReturnPath(): string | null {
  try {
    const path = globalThis.sessionStorage?.getItem(CONNECT_RETURN_PATH_KEY) ?? null;
    globalThis.sessionStorage?.removeItem(CONNECT_RETURN_PATH_KEY);
    return path !== null && path.startsWith('/') && !path.startsWith('//') ? path : null;
  } catch {
    return null;
  }
}

export function CalendarConnectReturnPage() {
  const search = useSearch();
  const { orgRef } = useBoardHost();
  const connections = useBoardConnectionOperations();
  const basePath = useBoardBasePath();
  const refresh = useRefreshCalendar(orgRef);
  const router = useRouter();
  const [outcome, setOutcome] = useState<ConnectOutcome | null>(null);
  const [back, setBack] = useState<string>(boardPath(basePath, BOARD_PAGES.meetings));
  const ran = useRef(false);

  const state = text(search.state);
  const code = text(search.code);
  const error = text(search.error);

  useEffect(() => {
    if (ran.current || orgRef === null) return;
    ran.current = true;
    void (async () => {
      if (state === null) {
        setOutcome({ kind: 'expired' });
        return;
      }
      if (await announced(state, code, error)) {
        window.close();
        return;
      }
      // Nobody is listening: this tab went to the provider itself. Finish here.
      const kept = takeReturnPath();
      if (kept !== null) setBack(kept);
      if (error !== null || code === null) {
        setOutcome(outcomeOfProviderError(error ?? 'access_denied'));
        return;
      }
      try {
        const { data } = await connections.completeOrgIntegrationOauth({
          path: { org_ref: orgRef },
          body: { state, code, redirect_uri: calendarConnectRedirectUri(basePath) },
          throwOnError: true,
        });
        const returned = text(data.return_path);
        if (
          kept === null &&
          returned !== null &&
          returned.startsWith('/') &&
          !returned.startsWith('//')
        ) {
          setBack(returned);
        }
        await refresh();
        setOutcome({ kind: 'connected', connection: data });
      } catch (failure) {
        setOutcome(outcomeOfCompleteFailure(failure));
      }
    })();
  }, [basePath, code, connections, error, orgRef, refresh, state]);

  if (outcome === null) return <div data-testid="calendar-connect-return" />;
  const goBack = () => router.history.push(back);
  return (
    <div data-testid="calendar-connect-return">
      <Dialog.Root open onOpenChange={(next) => !next && goBack()}>
        <Dialog.Content
          aria-label={w.connectTitle}
          width={448}
          className="flex flex-col gap-16"
          testId="calendar-connect-dialog"
        >
          <ConnectAnswer outcome={outcome} boardAccount={false} onClose={goBack} onAgain={goBack} />
        </Dialog.Content>
      </Dialog.Root>
    </div>
  );
}
